import { jsx } from 'react/jsx-runtime';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { Cross, CartFilled, Paw } from '@zooplus/icons';
import { getTestLabels } from '../../utils/getTestLabels.js';
import { LoadingIcon } from '../LoadingIcon.js';
import style from './IconButton.module.scss.js';

const iconMap = {
    x: jsx(Cross, { "aria-hidden": true }),
    cart: jsx(CartFilled, { "aria-hidden": true }),
    paw: jsx(Paw, { "aria-hidden": true }),
};
/**
 * [design (figma)](https://www.figma.com/design/MUnGlxUUspXSbRkFsEXWGQ/%F0%9F%92%A0-Core-Components?node-id=35-7250&node-type=section&t=L12XIxh9aK1lGzgC-0)
 * | [source code](https://src.private.zooplus.net/projects/FE/repos/ui-components/browse/src/components/Button/Button.tsx)
 *
 * @param {string} ariaLabel - A mandatory text label for the button for accessibility. **Must be translated in the current local language**.
 * @param {string} icon - (optional) Can be used to specify an icon instead of passing it in as children. Can be one of `x`, `paw` or `cart` (defaults to `x` if not provided **and** children not provided).
 * @param {string} size - (optional) Controls the button size affecting its height and width. Can be one of `small`, `medium` or `large` (defaults to `medium` if not provided).
 * @param {string} variant - (optional) Controls the button styling. Can be one of `primary`, `promo`, `ghost` or `inverse` (defaults to `primary` if not provided).
 * @param {boolean} isLoading - (optional) Set to `true` to show a loading/activity indicator.
 * @param {string} loadingIconType - (optional) Controls the activity indicator shown when `isLoading` is set to `true`.
 */
const IconButton = forwardRef((props, ref) => {
    const { children, className, ariaLabel = '', icon = 'x', size = 'medium', variant = 'primary', disabled = false, isLoading = false, loadingIconType = 'paw', ...otherProps } = props;
    const hasAriaLabel = ariaLabel.trim() !== '';
    const { componentTestLabel } = getTestLabels('IconButton');
    if (!hasAriaLabel) {
        console.log('IconButton component not displayed because no ariaLabel prop was provided');
        return null;
    }
    return (jsx("button", { "data-zta": componentTestLabel, "aria-label": ariaLabel, ref: ref, ...otherProps, type: otherProps.type || 'button', className: classNames(className, style['z-icon-button'], {
            [style[`z-icon-button--size-${size}`]]: size,
            [style[`z-icon-button--variant-${variant}`]]: variant,
            [style['z-icon-button--disabled']]: disabled,
            [style['z-icon-button--loading']]: isLoading,
        }), disabled: disabled || isLoading, children: isLoading ? (jsx(LoadingIcon, { className: style['z-btn__loading-icon'], iconType: loadingIconType })) : children ? (children) : (iconMap[icon]) }));
});
IconButton.displayName = 'IconButton';

export { IconButton };
//# sourceMappingURL=IconButton.js.map
